/* Metal Slug × Filippo Loreti — Mission signup landing
   Custom elements: <ms-intro-loader>, <ms-countdown>, <ms-signup>
   No dependencies. Safe to include more than once. */
(() => {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const pad = (n) => String(n).padStart(2, '0');

  /* Parses "2026-10-29T00:00:00" (visitor's local time) or with an explicit
     offset/Z ("2026-10-29T00:00:00-04:00"). Avoids Date.parse quirks. */
  const parseLaunchDate = (value) => {
    const m = String(value || '').trim().match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i
    );
    if (!m) return NaN;
    const [, y, mo, d, h = '0', mi = '0', s = '0', tz] = m;
    if (!tz) return new Date(+y, +mo - 1, +d, +h, +mi, +s).getTime();
    let offset = 0;
    if (tz.toUpperCase() !== 'Z') {
      const sign = tz[0] === '-' ? -1 : 1;
      const digits = tz.slice(1).replace(':', '');
      offset = sign * (parseInt(digits.slice(0, 2), 10) * 60 + parseInt(digits.slice(2), 10));
    }
    return Date.UTC(+y, +mo - 1, +d, +h, +mi, +s) - offset * 60000;
  };

  /* ---------------------------------------------------------------- */
  /* Countdown                                                         */
  /* ---------------------------------------------------------------- */
  class MsCountdown extends HTMLElement {
    connectedCallback() {
      this.target = parseLaunchDate(this.dataset.target);
      this.units = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      if (Number.isNaN(this.target)) return;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const ms = Math.max(0, this.target - Date.now());
      const values = {
        d: Math.floor(ms / 864e5),
        h: Math.floor(ms / 36e5) % 24,
        m: Math.floor(ms / 6e4) % 60,
        s: Math.floor(ms / 1e3) % 60,
      };
      Object.entries(values).forEach(([key, val]) => {
        const el = this.units[key];
        if (el && el.textContent !== pad(val)) el.textContent = pad(val);
      });
      if (ms === 0) {
        this.classList.add('is-live');
        window.clearInterval(this.timer);
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* Intro loader                                                      */
  /* ---------------------------------------------------------------- */
  class MsIntroLoader extends HTMLElement {
    connectedCallback() {
      this.timers = [];
      this.p = 0;
      this.storageKey = `ms-intro-seen:${this.dataset.sectionId}`;
      this.skipButton = this.querySelector('[data-ms-skip]');
      this.bar = this.querySelector('[role="progressbar"]');
      this.pct = this.querySelector('[data-ms-pct]');

      this.onSkip = () => this.skip();
      this.onKey = (event) => { if (event.key === 'Escape') this.skip(); };
      this.skipButton?.addEventListener('click', this.onSkip);

      if (window.Shopify && window.Shopify.designMode) {
        this.setupDesignMode();
        return;
      }

      if (this.hidden) return;
      this.start();
    }

    disconnectedCallback() {
      this.clear();
      document.documentElement.classList.remove('ms-intro-active');
      document.removeEventListener('keydown', this.onKey);
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    setupDesignMode() {
      this.hidden = true;
      this.onSelect = (event) => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.hidden = false;
        this.start({ loop: true });
      };
      this.onDeselect = (event) => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.finish(true);
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
    }

    clear() {
      window.clearInterval(this.ticker);
      this.timers.forEach(window.clearTimeout);
      this.timers = [];
    }

    later(fn, ms) {
      this.timers.push(window.setTimeout(fn, ms));
    }

    setProgress(p) {
      this.p = p;
      const whole = Math.floor(p);
      this.style.setProperty('--p', p.toFixed(1));
      this.bar?.setAttribute('aria-valuenow', String(whole));
      if (this.pct) this.pct.textContent = String(whole).padStart(3, '0');
    }

    start({ loop = false } = {}) {
      this.clear();
      this.loop = loop;
      this.classList.remove('is-starting', 'is-exiting');
      document.documentElement.classList.add('ms-intro-active');
      document.addEventListener('keydown', this.onKey);
      this.setProgress(0);

      if (prefersReducedMotion()) {
        this.setProgress(100);
        this.finish(true);
        return;
      }

      this.ticker = window.setInterval(() => {
        const p = this.p;
        const base = p < 30 ? 3.6 : p < 60 ? 2.4 : p < 78 ? 0.9 : p < 94 ? 3.2 : 1.6;
        const next = Math.min(100, p + base * (0.6 + Math.random() * 0.8));
        this.setProgress(next);
        if (next >= 100) this.complete();
      }, 70);
    }

    skip() {
      if (this.hidden || this.classList.contains('is-starting')) return;
      this.clear();
      this.setProgress(100);
      this.complete();
    }

    complete() {
      window.clearInterval(this.ticker);
      this.later(() => this.classList.add('is-starting'), 120);
      this.later(() => this.classList.add('is-exiting'), 1300);
      this.later(() => this.finish(), 2100);
    }

    finish(immediate = false) {
      this.clear();
      this.hidden = true;
      this.classList.remove('is-starting', 'is-exiting');
      document.documentElement.classList.remove('ms-intro-active');
      document.removeEventListener('keydown', this.onKey);
      if (!(window.Shopify && window.Shopify.designMode)) {
        try { window.sessionStorage.setItem(this.storageKey, '1'); } catch (e) { /* storage blocked */ }
      }
      if (this.loop && !immediate) this.later(() => { this.hidden = false; this.start({ loop: true }); }, 900);
      document.dispatchEvent(new CustomEvent('ms:intro:done'));
      const target = document.querySelector('[data-ms-autofocus]');
      if (!immediate && target && !this.loop) target.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------- */
  /* Debug logging: add ?ms_debug=1 to the URL (persists for the tab)  */
  /* ---------------------------------------------------------------- */
  const DEBUG = (() => {
    try {
      if (/[?&]ms_debug=1/.test(window.location.search)) window.sessionStorage.setItem('ms_debug', '1');
      if (/[?&]ms_debug=0/.test(window.location.search)) window.sessionStorage.removeItem('ms_debug');
      return window.sessionStorage.getItem('ms_debug') === '1';
    } catch (e) {
      return /[?&]ms_debug=1/.test(window.location.search);
    }
  })();
  const log = (...args) => { if (DEBUG) console.log('%c[metal-slug]', 'color:#F28C1B;font-weight:bold', ...args); };

  /* ---------------------------------------------------------------- */
  /* Marketing consent (Shopify Customer Privacy API)                  */
  /* ---------------------------------------------------------------- */
  const marketingAllowed = () => new Promise((resolve) => {
    let done = false;
    const read = () => {
      if (done) return;
      done = true;
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      const allowed = cp && typeof cp.marketingAllowed === 'function' ? cp.marketingAllowed() !== false : true;
      log('consent: marketingAllowed =', allowed, cp ? '(Customer Privacy API)' : '(no consent API on page → allowed)');
      resolve(allowed);
    };
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) return read();
    if (shopify && typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], read);
      window.setTimeout(read, 3000);
      return;
    }
    read();
  });

  /* ---------------------------------------------------------------- */
  /* Meta (Facebook) Pixel                                             */
  /* ---------------------------------------------------------------- */
  const pixelInitialized = (id) => {
    try {
      const state = window.fbq && typeof window.fbq.getState === 'function' ? window.fbq.getState() : null;
      return !!(state && state.pixels && state.pixels.some((px) => String(px.id) === String(id)));
    } catch (e) {
      return false;
    }
  };

  const MetaPixel = {
    ready: null,
    initialized: false,
    pendingLead: null,
    waitingForConsent: false,

    init(config) {
      if (this.ready) return this.ready;
      if (window.Shopify && window.Shopify.designMode) {
        log('meta: theme editor → pixel disabled');
        return (this.ready = Promise.resolve(false));
      }
      this.ready = marketingAllowed().then((allowed) => {
        if (!allowed) {
          this.ready = null;
          this.waitForConsent(config);
          return false;
        }
        return this.boot(config);
      });
      return this.ready;
    },

    waitForConsent(config) {
      if (this.waitingForConsent) return;
      this.waitingForConsent = true;
      log('meta: waiting for marketing consent (cookie banner) before loading the pixel');
      document.addEventListener('visitorConsentCollected', (e) => {
        this.waitingForConsent = false;
        if (!(e.detail && e.detail.marketingAllowed)) return;
        log('meta: consent granted');
        this.init(config).then((ok) => {
          if (ok && this.pendingLead) {
            const lead = this.pendingLead;
            this.pendingLead = null;
            this.send(config, lead);
          }
        });
      });
    },

    boot(config) {
      const id = String(config.pixelId).trim();
      if (!/^\d{6,20}$/.test(id)) {
        console.warn('[metal-slug] Meta Pixel ID looks invalid:', id, '— expected the numeric ID from Events Manager.');
        return false;
      }
      let loadedHere = false;
      if (!window.fbq) {
        if (!config.loadPixel) {
          console.warn('[metal-slug] Meta Pixel: fbq() is not on the page and "Load the pixel on this page" is off — Lead cannot be sent.');
          return false;
        }
        /* Official Meta base code */
        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
          if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
          t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        loadedHere = true;
        log('meta: fbevents.js injected');
      }
      /* Initialise OUR pixel even when another app already created fbq(); otherwise trackSingle() is silently dropped. */
      if (!pixelInitialized(id)) {
        window.fbq('init', id);
        window.fbq('trackSingle', id, 'PageView');
        log('meta: init + PageView for pixel', id, loadedHere ? '(pixel loaded by this page)' : '(fbq already present)');
      } else {
        log('meta: pixel', id, 'already initialised by another script');
      }
      this.initialized = true;
      return true;
    },

    send(config, lead) {
      window.fbq('trackSingle', String(config.pixelId).trim(), config.event || 'Lead', lead.data, { eventID: lead.eventId });
      log('meta: sent', config.event || 'Lead', lead.data, 'eventID =', lead.eventId);
    },

    async track(config) {
      const lead = {
        eventId: `ms-lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        data: { content_name: config.contentName || 'Newsletter signup', content_category: 'newsletter' },
      };
      const ok = await this.init(config);
      if (!ok || !window.fbq) {
        if (this.waitingForConsent) {
          this.pendingLead = lead;
          log('meta: Lead queued until the visitor accepts marketing cookies');
        } else {
          log('meta: Lead NOT sent (pixel unavailable — see warnings above)');
        }
        return;
      }
      this.send(config, lead);
    },
  };

  /* ---------------------------------------------------------------- */
  /* Klaviyo client subscription API                                   */
  /* ---------------------------------------------------------------- */
  const KLAVIYO_REVISION = '2026-07-15';

  const subscribeKlaviyo = async (config, email) => {
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(config.publicKey)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: config.source || 'Website signup',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: config.listId } } },
        },
      }),
    });
    log('klaviyo: response', response.status, response.status === 202 ? '(accepted)' : '');
    if (response.ok) return { type: 'success' };
    if (response.status === 400) return { type: 'error' };
    return { type: 'error', message: config.requestError };
  };

  /* ---------------------------------------------------------------- */
  /* Signup (progressive enhancement of {% form 'customer' %})         */
  /* ---------------------------------------------------------------- */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MIN_LOADING_MS = 900;

  class MsSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.button = this.querySelector('button[type="submit"]');
      this.formState = this.querySelector('[data-ms-form-state]');
      this.successState = this.querySelector('[data-ms-success-state]');
      this.successHeading = this.querySelector('[data-ms-success-heading]');
      this.message = this.querySelector('[data-ms-message]');
      this.errorTemplate = this.querySelector('[data-ms-error-template]');
      this.scene = this.closest('.ms-hero');
      if (!this.form || !this.input) return;

      this.onSubmit = (event) => this.submit(event);
      this.onInput = () => { if (this.state === 'error') this.setState('idle'); };
      this.onReset = () => this.reset();

      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);
      this.querySelector('[data-ms-reset]')?.addEventListener('click', this.onReset);
      const marker = this.querySelector('[data-ms-initial-state]');
      this.state = (marker && marker.dataset.msInitialState) || 'idle';

      try {
        this.integrations = JSON.parse(this.querySelector('[data-ms-integrations]')?.textContent || '{}');
      } catch (e) {
        this.integrations = {};
      }
      log('integrations:', this.integrations);
      if (!this.integrations.meta) log('meta: no Pixel ID set in the "Meta Pixel" block → Lead will not fire');
      if (this.integrations.meta) MetaPixel.init(this.integrations.meta);
      /* No-JS / challenge fallback landed back on the page with a successful post. */
      if (this.state === 'success' && /[?&]customer_posted=true/.test(window.location.search)) this.onLead('');
    }

    onLead(email) {
      document.dispatchEvent(new CustomEvent('ms:signup:success', { detail: { email } }));
      if (this.integrations.meta) MetaPixel.track(this.integrations.meta);
      if (email && window.klaviyo && typeof window.klaviyo.identify === 'function') {
        try { window.klaviyo.identify({ email }); } catch (e) { /* onsite tracking optional */ }
      }
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        try { window.Shopify.analytics.publish('ms_newsletter_signup', { source: 'metal-slug-landing' }); } catch (e) { /* noop */ }
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
    }

    shake() {
      if (!this.scene || prefersReducedMotion()) return;
      this.scene.classList.remove('is-shaking');
      void this.scene.offsetWidth;
      this.scene.classList.add('is-shaking');
      window.setTimeout(() => this.scene.classList.remove('is-shaking'), 320);
    }

    setState(state, errorText) {
      this.state = state;
      this.dataset.state = state;

      const loading = state === 'loading';
      this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');
      this.form.setAttribute('aria-busy', loading ? 'true' : 'false');

      const isError = state === 'error';
      this.input.setAttribute('aria-invalid', isError ? 'true' : 'false');
      this.message.replaceChildren();
      if (isError && this.errorTemplate) {
        const alert = this.errorTemplate.content.firstElementChild.cloneNode(true);
        if (errorText) alert.querySelector('[data-ms-error-text]').textContent = errorText;
        this.message.appendChild(alert);
      }

      const success = state === 'success';
      this.formState.hidden = success;
      this.successState.hidden = !success;
      if (success) this.successHeading?.focus();
    }

    async submit(event) {
      event.preventDefault();
      if (this.state === 'loading') return;

      const email = this.input.value.trim();
      this.input.value = email;
      this.shake();

      if (!EMAIL_RE.test(email)) {
        this.setState('error');
        this.input.focus();
        return;
      }

      this.setState('loading');
      const started = performance.now();

      let outcome;
      const klaviyo = this.integrations.klaviyo;
      if (klaviyo) {
        try {
          outcome = await subscribeKlaviyo(klaviyo, email);
        } catch (e) {
          outcome = { type: 'error', message: klaviyo.requestError };
        }
        /* Mirror to Shopify customers in the background; never block or redirect on it. */
        if (outcome.type === 'success' && klaviyo.alsoShopify) this.post().catch(() => {});
      } else {
        try {
          outcome = await this.post();
        } catch (e) {
          outcome = { type: 'native' };
        }
      }

      const wait = MIN_LOADING_MS - (performance.now() - started);
      if (wait > 0) await new Promise((r) => window.setTimeout(r, wait));

      if (outcome.type === 'native') {
        /* Bot-protection challenge or unexpected response: let Shopify handle it. */
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }
      if (outcome.type === 'success') {
        this.setState('success');
        this.onLead(email);
        return;
      }
      this.setState('error', outcome.message);
      this.input.focus();
    }

    async post() {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(this.form)),
        headers: { Accept: 'text/html', 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'same-origin',
      });

      const finalUrl = response.url || '';
      if (/\/challenge(\/|\?|$)/.test(finalUrl)) return { type: 'native' };
      if (/[?&]customer_posted=true/.test(finalUrl)) return { type: 'success' };

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remote = doc.getElementById(this.id);
      if (!remote) return { type: 'native' };

      const marker = remote.querySelector('[data-ms-initial-state]');
      const initial = marker ? marker.getAttribute('data-ms-initial-state') : '';
      if (initial === 'success') return { type: 'success' };
      if (initial === 'error') {
        const text = remote.querySelector('[data-ms-message] [data-ms-error-text]');
        return { type: 'error', message: text ? text.textContent.trim() : '' };
      }
      return { type: 'native' };
    }

    reset() {
      this.input.value = '';
      this.setState('idle');
      this.input.focus();
    }
  }

  if (!customElements.get('ms-countdown')) customElements.define('ms-countdown', MsCountdown);
  if (!customElements.get('ms-intro-loader')) customElements.define('ms-intro-loader', MsIntroLoader);
  if (!customElements.get('ms-signup')) customElements.define('ms-signup', MsSignup);
})();
